/**
 * @file campUSD integration with Nucleus SDK
 * @description Configuration and functions for bridging USDC <-> campUSD using Nucleus SDK
 */

import {
  prepareDepositAndBridgeTransactionData,
  prepareBridgeAndWithdrawTransactionData,
  prepareApproveDepositToken,
  VaultKeys,
  type VaultKey,
  type DepositAndBridgeTransactionData,
  type BridgeAndWithdrawTransactionData,
  type ApproveDepositTokenTransactionData,
} from "@molecularlabs/nucleus-frontend";
import { Address } from "viem";

// campUSD Nucleus configuration
export const CAMPUSD_VAULT_KEY: VaultKey = VaultKeys.CAMPUSD;

// Chain IDs
export const ETHEREUM_CHAIN_ID = 1;
export const CAMP_CHAIN_ID = 484;

// USDC token symbol for Nucleus SDK
export const USDC_TOKEN_SYMBOL = "USDC";

/**
 * Interface for campUSD bridge parameters
 */
export interface CampUSDDepositParams {
  userAddress: Address;
  depositAmount: string; // Amount in USDC as string (e.g., "100.00")
  slippage?: number; // Optional slippage in basis points (e.g., 50 for 0.5%)
}

export interface CampUSDWithdrawParams {
  userAddress: Address;
  withdrawAmount: string; // Amount in campUSD shares as string
  slippage?: number; // Optional slippage in basis points
}

/**
 * Prepares transaction data for depositing USDC and bridging to campUSD on Camp Network
 * This implements the depositAndBridge flow: USDC (Ethereum) -> campUSD (Camp Network)
 */
export async function prepareCampUSDDeposit(
  params: CampUSDDepositParams,
): Promise<DepositAndBridgeTransactionData> {
  return await prepareDepositAndBridgeTransactionData({
    vaultKey: CAMPUSD_VAULT_KEY,
    userAddress: params.userAddress,
    depositTokenSymbol: USDC_TOKEN_SYMBOL,
    depositAmount: params.depositAmount,
    sourceChainId: ETHEREUM_CHAIN_ID,
    destinationChainId: CAMP_CHAIN_ID,
    slippage: params.slippage,
  });
}

/**
 * Prepares transaction data for withdrawing campUSD and bridging back to USDC
 * This implements the bridgeAndWithdraw flow: campUSD (Camp Network) -> USDC (Ethereum)
 */
export async function prepareCampUSDWithdraw(
  params: CampUSDWithdrawParams,
): Promise<BridgeAndWithdrawTransactionData> {
  return await prepareBridgeAndWithdrawTransactionData({
    vaultKey: CAMPUSD_VAULT_KEY,
    userAddress: params.userAddress,
    offerAmount: params.withdrawAmount,
    wantTokenSymbol: USDC_TOKEN_SYMBOL,
    sourceChainId: CAMP_CHAIN_ID,
    destinationChainId: ETHEREUM_CHAIN_ID,
    slippage: params.slippage,
  });
}

/**
 * Prepares approval transaction for USDC before deposit
 */
export async function prepareCampUSDDepositApproval(
  params: CampUSDDepositParams,
): Promise<ApproveDepositTokenTransactionData> {
  return await prepareApproveDepositToken({
    vaultKey: CAMPUSD_VAULT_KEY,
    depositTokenSymbol: USDC_TOKEN_SYMBOL,
    depositAmount: params.depositAmount,
    chainId: ETHEREUM_CHAIN_ID,
  });
}

/**
 * campUSD token configuration for bridge UI
 */
export const CAMPUSD_TOKEN_CONFIG = {
  id: "campusd",
  symbol: "campUSD",
  name: "Camp USD",
  icon: "/usdc.svg", // Using USDC icon since campUSD is backed by USDC
  decimals: 18, // campUSD uses 18 decimals (standard for Nucleus vaults)
  description: "USD-backed stablecoin vault token powered by Nucleus",

  // Ethereum configuration (source chain for deposits)
  ethereum: {
    isNative: false,
    // campUSD vault shares contract address on Ethereum will be provided by Nucleus SDK
    contractAddress: undefined, // Will be fetched from Nucleus SDK
    symbol: "campUSD",
    name: "CampUSD",
    depositToken: {
      symbol: "USDC",
      name: "USDC",
      contractAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" as Address,
      decimals: 6,
    },
  },

  // Camp Network configuration (destination chain for deposits)
  camp: {
    isNative: false,
    // campUSD vault shares contract address on Camp Network will be provided by Nucleus SDK
    contractAddress: undefined, // Will be fetched from Nucleus SDK
    symbol: "campUSD",
    name: "CampUSD",
    withdrawToken: {
      symbol: "USDC",
      name: "USDC",
      // Note: For withdrawals, USDC would be received on Ethereum mainnet
      contractAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48" as Address,
      decimals: 6,
    },
  },
} as const;

/**
 * Helper function to get campUSD token configuration for a specific direction
 */
export function getCampUSDTokenConfig(
  direction: "eth-to-camp" | "camp-to-eth",
) {
  if (direction === "eth-to-camp") {
    return {
      source: CAMPUSD_TOKEN_CONFIG.ethereum,
      destination: CAMPUSD_TOKEN_CONFIG.camp,
      sourceChainId: ETHEREUM_CHAIN_ID,
      destinationChainId: CAMP_CHAIN_ID,
    };
  } else {
    return {
      source: CAMPUSD_TOKEN_CONFIG.camp,
      destination: CAMPUSD_TOKEN_CONFIG.ethereum,
      sourceChainId: CAMP_CHAIN_ID,
      destinationChainId: ETHEREUM_CHAIN_ID,
    };
  }
}

/**
 * Type for campUSD bridge operations
 */
export type CampUSDBridgeDirection = "deposit" | "withdraw";

export type CampUSDBridgeOperation = {
  direction: CampUSDBridgeDirection;
  sourceChain: number;
  destinationChain: number;
  tokenSymbol: string;
  vaultKey: VaultKey;
};

export const CAMPUSD_OPERATIONS: Record<
  CampUSDBridgeDirection,
  CampUSDBridgeOperation
> = {
  deposit: {
    direction: "deposit",
    sourceChain: ETHEREUM_CHAIN_ID,
    destinationChain: CAMP_CHAIN_ID,
    tokenSymbol: USDC_TOKEN_SYMBOL,
    vaultKey: CAMPUSD_VAULT_KEY,
  },
  withdraw: {
    direction: "withdraw",
    sourceChain: CAMP_CHAIN_ID,
    destinationChain: ETHEREUM_CHAIN_ID,
    tokenSymbol: USDC_TOKEN_SYMBOL,
    vaultKey: CAMPUSD_VAULT_KEY,
  },
};
